<template>
  <div>
    <b-card no-body>
      <div class="mt-1 mb-0 mx-1">
        <b-row>
          <b-col
            cols="12"
            md="6"
            xl="4"
          >
            <div
              v-if="bagId"
              class="mb-1"
            >
              <div class="font-weight-bolder">
                {{ bagId }}
              </div>
            </div>
            <b-form-group
              label="Waybill Number"
              label-for="v-ref-no"
              class="mb-1"
            >
              <b-form-input
                id="v-ref-no"
                v-model="searchWaybill"
                placeholder="Search"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </div>

      <b-overlay
        id="overlay-background"
        blur="2px"
        opacity="0.3"
        rounded="sm"
        variant="light"
        :show="loading"
      >
        <b-table
          :items="bagOrders"
          :fields="bagOrdersFields"
          :filter="searchWaybill"
          striped
          hover
          responsive
          show-empty
          class="hide-vertical-scroll mb-0"
        >
          <template #empty>
            <div class="text-center p-2 text-muted">
              {{ loading ? 'Loading orders...' : 'No orders found in this bag.' }}
            </div>
          </template>

          <template #cell(order.waybill_number)="data">
            <b-link
              class="font-weight-bold"
              @click="openOrderModal(data.item.order_id || data.item.order.id)"
            >
              {{ data.value }}
            </b-link>
          </template>

          <template #cell(order.cod)="data">
            {{ __numberWithCommas(data.value, true) }}
          </template>

          <template #cell(order.collected_cod)="data">
            {{ __numberWithCommas(data.value, true) }}
          </template>

          <template #cell(order.return_charge)="data">
            {{ __numberWithCommas(data.value, true) }}
          </template>

          <template #cell(order.delivery_charge)="data">
            {{ __numberWithCommas(data.value, true) }}
          </template>

          <template #cell(order.weight)="data">
            {{ data.value ? data.value.toLocaleString() : '---' }}
          </template>

          <template #cell(order.order_current_status.key)="data">
            <order-status-badge :status-key="data.value" />
          </template>

        </b-table>

        <!-- Pagination -->
        <div class="mx-2 mb-2 mt-2">
          <b-row>
            <b-col
              cols="12"
              md="3"
              class="d-flex align-items-center justify-content-sm-start"
            >
              <label>Per page</label>
              <v-select
                v-model="perPage"
                :options="pageOptions"
                :clearable="false"
                class="per-page-selector d-inline-block ml-1 width-100 mr-1"
              />
            </b-col>
            <b-col
              cols="12"
              md="9"
              class="d-flex align-items-center justify-content-sm-end"
            >
              <span class="text-muted pagination-text mt-1 mt-md-0 mr-1">
                Showing {{ meta.from || 0 }} to {{ meta.to || 0 }} of {{ meta.total || 0 }} entries
              </span>
              <b-pagination
                v-model="page"
                :total-rows="meta.total || 0"
                :per-page="perPage"
                first-number
                last-number
                class="mb-0 mt-1 mt-sm-0"
                prev-class="prev-item"
                next-class="next-item"
              >
                <template #prev-text>
                  <feather-icon
                    icon="ChevronLeftIcon"
                    size="18"
                  />
                </template>
                <template #next-text>
                  <feather-icon
                    icon="ChevronRightIcon"
                    size="18"
                  />
                </template>
              </b-pagination>
            </b-col>
          </b-row>
        </div>
      </b-overlay>
      <div class="p-2 d-flex justify-content-start bg-light rounded-bottom border-top">
        <b-button
          variant="outline-secondary"
          @click="$router.push({ name: 'return-bag-reporting' })"
        >
          <feather-icon
            icon="ArrowLeftIcon"
            class="mr-50"
          />
          <span>Back to Reporting</span>
        </b-button>
      </div>
    </b-card>
    <view-order-modal ref="viewOrderModal" />
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BTable, BOverlay, BButton, BFormGroup, BFormInput, BLink, BPagination,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import OrderStatusBadge from '@core/components/status/OrderStatusBadge.vue'
import ViewOrderModal from '@/components/AllOrders/ViewOrderModal.vue'
import { RepositoryFactory } from '@/repository/RepositoryFactory'
import SuccessMessage from '@core/mixins/SuccessMessage'
import ErrorMessage from '@core/mixins/ErrorMessage'
import Filter from '@core/mixins/filter'

const sortingRepository = RepositoryFactory.get('sorting')

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BTable,
    BOverlay,
    BButton,
    BFormGroup,
    BFormInput,
    BLink,
    BPagination,
    vSelect,
    OrderStatusBadge,
    ViewOrderModal,
  },
  mixins: [SuccessMessage, ErrorMessage, Filter],
  data() {
    return {
      id: null,
      bagId: '',
      loading: false,
      searchWaybill: '',
      bagOrders: [],
      meta: {},
      page: 1,
      perPage: 20,
      pageOptions: [10, 20, 50, 100],
      bagOrdersFields: [
        { key: 'order.waybill_number', label: 'WAYBILL NUMBER', sortable: true },
        { key: 'order.orderable.business_name', label: 'MERCHANT BUSINESS', sortable: true },
        { key: 'order.customer_name', label: 'CUSTOMER NAME', sortable: true },
        { key: 'order.customer_phone', label: 'CUSTOMER CONTACT', sortable: true },
        {
          key: 'order.cod', label: 'COD', thClass: 'text-right', tdClass: 'text-right',
        },
        {
          key: 'order.collected_cod', label: 'COLLECTED COD', thClass: 'text-right', tdClass: 'text-right',
        },
        {
          key: 'order.return_charge', label: 'RETURN CHARGE', thClass: 'text-right', tdClass: 'text-right',
        },
        {
          key: 'order.weight', label: 'WEIGHT', thClass: 'text-right', tdClass: 'text-right',
        },
        { key: 'order.order_current_status.key', label: 'STATUS' },
      ],
    }
  },
  watch: {
    page() {
      this.fetchOrders()
    },
    perPage() {
      this.page = 1
      this.fetchOrders()
    },
  },
  created() {
    this.id = this.$route.params.id
    this.bagId = this.$route.query.bag_id || ''
    if (this.id) {
      this.fetchOrders()
    }
  },
  methods: {
    async fetchOrders() {
      this.loading = true
      try {
        const { data } = await sortingRepository.getReturnBagOrders(
          this.id,
          this.page,
          this.perPage,
        )
        if (data && data.data) {
          this.bagOrders = data.data || []
          this.meta = data.meta || {}
          if (data.bag_id) {
            this.bagId = data.bag_id
          }
        } else {
          this.bagOrders = []
          this.meta = {}
        }
      } catch (error) {
        this.convertAndNotifyError(error)
      } finally {
        this.loading = false
      }
    },
    openOrderModal(id) {
      if (id) {
        this.$refs.viewOrderModal.openModal(id)
      }
    },
  },
}
</script>

<style lang="scss">
@import '~@core/scss/vue/libs/vue-select.scss';

.width-100 {
  width: 100px;
}
</style>
